import { ApiError, apiFetch } from "../api/client";
import { formatFullName } from "../funnel/nomAffiche";

/**
 * ═══ LE CARNET DE RENCONTRES ═══
 *
 *   GET  /v2/domains/:d/events/:e/contacts/me                   api-v1   les fiches
 *   GET  …/contacts/me?createExport=1                           api-v1   le tableur
 *   POST /domains/:d/events/:e/users/:moi/links/:lui            api-v1   la note, le lien
 *   GET  /v2/domains/:d/events/:e/users/badges/:code             api-v1   qui porte ce badge
 *
 * ⚠️ TROIS ADRESSES, UN SEUL SERVEUR, ET DEUX PRÉFIXES DIFFÉRENTS. La lecture est sous
 * `/v2/`, l'écriture ne l'est pas : ce sont deux arbres de routes montés séparément dans
 * api-v1 (`routesV2/` et `routes/`). Écrire l'une sur le modèle de l'autre répond 404.
 *
 * ⚠️ LES CHAMPS DE LA FICHE SONT RÉSOLUS PAR LE SERVEUR, ET ON NE LES REFAIT PAS. La
 * configuration vit au back office (`FO_MEETING_CONTACT_DISPLAY_FIELDS`) et désigne des
 * champs NATIFS ou ÉTENDUS, par leur identifiant du BO (`U.firstName`, `C.name`,
 * `U.extended.<clé>`…). Le serveur les résout en `[{key, label, value}]` — libellés
 * traduits compris, valeurs multi-choix jointes. Un écran qui rejouerait cette
 * résolution redonnerait une seconde vérité, plus pauvre, sur la question la plus
 * sensible de cette page : ce qu'un badge scanné RÉVÈLE de quelqu'un.
 *
 * ⚠️ D'OÙ L'ABSENCE DE RÉGLAGES « SOCIÉTÉ / FONCTION / E-MAIL » DANS LE PANNEAU. La
 * maquette en propose quatre, en booléens ; le produit, lui, laisse choisir N'IMPORTE
 * QUEL champ au back office. Quatre interrupteurs V2 auraient soit ignoré cette
 * configuration, soit prétendu la piloter sans que le serveur les lise.
 */

/** Une personne rencontrée, telle que la route la rend. */
export type Contact = {
  id: number;
  /** Ce que le badge révèle, dans l'ordre voulu par l'organisateur. */
  fiche: ReadonlyArray<{ key: string; label: string; value: string }>;
  /** Quand le lien a été créé — le moment du scan. */
  when: string;
  /** La note personnelle du porteur sur cette rencontre. Vide s'il n'y en a pas. */
  note: string;
  /**
   * Le nom, pour l'en-tête de la fiche et ses initiales.
   *
   * ⚠️ IL PEUT ÊTRE VIDE, et c'est fréquent : un compte créé à la volée n'a qu'un email.
   * La carte se désigne alors par son identifiant plutôt que par un blanc — un contact
   * sans titre est un contact qu'on ne peut plus distinguer des autres.
   */
  name: string;
  /** Le même nom au format d'AFFICHAGE « Prénom NOM » (`name` reste la valeur d'origine, cherchée et triée). */
  label: string;
};

type Raw = {
  id?: number;
  firstName?: string | null;
  lastName?: string | null;
  comment?: string | null;
  linkedAt?: string | null;
  fiche?: ReadonlyArray<{
    key?: unknown;
    label?: unknown;
    value?: unknown;
  }> | null;
};

const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");

export function contactDe(raw: Raw): Contact | null {
  if (typeof raw?.id !== "number") return null;
  const name = [text(raw.firstName), text(raw.lastName)]
    .filter(Boolean)
    .join(" ");
  return {
    id: raw.id,
    name: name || `Contact ${raw.id}`,
    label: formatFullName(raw.firstName, raw.lastName) || name || `Contact ${raw.id}`,
    when: text(raw.linkedAt),
    note: text(raw.comment),
    /* ⚠️ LES CHAMPS VIDES SONT ÉCARTÉS ICI, PAS À L'AFFICHAGE. Le serveur rend TOUTE la
       configuration, valeur vide comprise : rendus tels quels, la moitié des fiches
       s'affichait en « Société : » suivi de rien, ce qui se lit comme une donnée perdue
       plutôt que comme une donnée absente. */
    fiche: (raw.fiche ?? []).flatMap((f) => {
      const value = text(f?.value);
      const label = text(f?.label);
      return value && label
        ? [{ key: text(f?.key) || label, label, value }]
        : [];
    }),
  };
}

export async function fetchContacts(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<Contact[]> {
  const answer = await apiFetch<ReadonlyArray<Raw> | null>(
    `/v2/domains/${domainId}/events/${eventId}/contacts/me`,
    { signal },
  );
  return (answer ?? []).flatMap((c) => {
    const contact = contactDe(c);
    return contact ? [contact] : [];
  });
}

/**
 * LE TABLEUR DU CARNET — la MÊME route, avec un paramètre.
 *
 * ⚠️ ELLE NE REND PAS LE FICHIER MAIS SON ADRESSE. Le serveur écrit le classeur sur S3
 * et rend un lien signé : rien à assembler ici, et surtout pas un second export dont les
 * colonnes divergeraient de celles du back office — le serveur reprend EXACTEMENT les
 * champs de la fiche, dans l'ordre, plus la date et le commentaire.
 */
export async function exportContacts(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<string> {
  const url = await apiFetch<string | null>(
    `/v2/domains/${domainId}/events/${eventId}/contacts/me?createExport=1`,
    { signal },
  );
  if (typeof url !== "string" || !url) {
    throw new Error("L'export n'a pas pu être préparé.");
  }
  return url;
}

/**
 * LA NOTE D'UNE RENCONTRE.
 *
 * ⚠️ ON RE-POSTE LE LIEN, ON NE LE MODIFIE PAS. C'est ce que fait la V1, et le service
 * derrière est un `upsert` : le lien existant est MIS À JOUR avec le corps envoyé. Il
 * n'y a pas de route de modification à part, et en inventer une répondrait 404.
 */
export async function saveNote(
  domainId: number,
  eventId: number,
  moi: number,
  contactId: number,
  note: string,
): Promise<void> {
  await apiFetch(
    `/domains/${domainId}/events/${eventId}/users/${moi}/links/${contactId}`,
    { method: "POST", body: { comment: note } },
  );
}

/**
 * QUI PORTE CE BADGE — la recherche du scan.
 *
 * ⚠️ ELLE REND LA MÊME `fiche` QUE LE CARNET, résolue par le serveur : la personne qu'on
 * vient de scanner se présente donc exactement comme elle apparaîtra dans la liste.
 * L'écran de scan n'a aucune règle d'affichage à lui.
 *
 * ⚠️ UN CODE INCONNU N'EST PAS UNE PANNE. La route répond « User not found » : c'est le
 * cas courant d'un badge d'un AUTRE événement, ou d'un code mal saisi. Il se dit, il ne
 * se rejoue pas.
 */
export async function fetchByBadge(
  domainId: number,
  eventId: number,
  code: string,
  signal?: AbortSignal,
): Promise<Contact | null> {
  try {
    const raw = await apiFetch<Raw | null>(
      `/v2/domains/${domainId}/events/${eventId}/users/badges/${encodeURIComponent(code)}`,
      { signal },
    );
    return raw ? contactDe(raw) : null;
  } catch (error) {
    /**
     * ⚠️ « PERSONNE NE PORTE CE CODE » N'EST PAS UNE PANNE, ET LA ROUTE LE DIT EN 400.
     * Laissée remonter, l'exception affichait la phrase du serveur — « User not found »,
     * en anglais, sur un écran français — là où l'écran a la sienne, qui nomme le code
     * lu. Mesuré : c'est ce qui s'affichait.
     *
     * ⚠️ MAIS SEULEMENT CE CAS-LÀ. Une session expirée, une coupure, un 500 : ceux-là
     * doivent continuer de remonter, sinon un badge parfaitement valide se lit
     * « introuvable » et on cherche du côté du badge pendant que le serveur est à terre.
     */
    const notFound =
      error instanceof ApiError &&
      error.status < 500 &&
      /not found|introuvable|no user/i.test(error.message);
    if (notFound) return null;
    throw error;
  }
}

/**
 * AJOUTER QUELQU'UN À SON CARNET.
 *
 * ⚠️ C'EST LA MÊME ROUTE QUE LA NOTE, et ce n'est pas un raccourci : le service fait un
 * `upsert`. Scanner deux fois la même personne ne crée donc pas deux fiches, et
 * n'efface pas la note déjà prise puisqu'on n'envoie rien.
 */
export async function lierContact(
  domainId: number,
  eventId: number,
  moi: number,
  contactId: number,
): Promise<void> {
  await apiFetch(
    `/domains/${domainId}/events/${eventId}/users/${moi}/links/${contactId}`,
    { method: "POST", body: {} },
  );
}

/**
 * LE CODE QUE PORTE UN QR — car tous ne portent pas qu'un code.
 *
 * ⚠️ UN BADGE IMPRIMÉ PAR LA V1 PEUT ENCODER UNE ADRESSE, pas le code nu : la V1 le sait
 * et prend ce qui suit le `=` (elle traite aussi un `-` comme un `=`, pour une génération
 * plus ancienne encore — dans une adresse seulement : un code nu ne se coupe pas). Les
 * badges de la V2 encodent `UserEvents.code` tel quel. Un
 * scanner qui n'accepterait que le second refuserait les badges déjà imprimés, sur place,
 * sans que personne comprenne pourquoi.
 *
 * Pure, donc testée sans caméra — c'est la seule règle de cet écran.
 */
export function codeDuQr(lu: string): string {
  const raw = lu.trim();
  /* ⚠️ LE TIRET NE SÉPARE QUE DANS UNE ADRESSE. Un code nu peut en porter lui-même
     (`claude-dev-706`) : le couper faisait chercher « 706 », et personne ne le porte. */
  const adresse = raw.includes("/");
  /* Le DERNIER tiret : une adresse qui contient `fo-meeting` n'en a pas qu'un. */
  const dash = raw.lastIndexOf("-");
  const normalise =
    adresse && dash >= 0 && !raw.includes("=")
      ? `${raw.slice(0, dash)}=${raw.slice(dash + 1)}`
      : raw;
  if (!normalise.includes("=")) return raw;
  const after = normalise.split("=").pop() ?? "";
  /* Une adresse qui finit par « = » ne porte pas de code : on rend ce qu'on a lu plutôt
     que la chaîne vide, et la recherche dira qu'elle ne trouve personne. */
  return after.trim() || raw;
}
